import React, { useState, useEffect } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SearchCommandPalette from '../../components/ui/SearchCommandPalette';
import { NotificationProvider, useNotification } from '../../components/ui/NotificationToast';
import { officesService } from '../../services/dataService';

// Import page components
import CategoryTabs from './components/CategoryTabs';

import BulkActionsBar from './components/BulkActionsBar';
import OfficeDetailsModal from './components/OfficeDetailsModal';
import DragDropOfficeList from './components/DragDropOfficeList';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';

const OfficesManagementContent = () => {
  const { success, error, warning } = useNotification();

  // UI State
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // Data State
  const [offices, setOffices] = useState([]);
  const [filteredOffices, setFilteredOffices] = useState([]);
  const [activeCategory, setActiveCategory] = useState('board');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOffices, setSelectedOffices] = useState([]);

  // Modal State
  const [selectedOffice, setSelectedOffice] = useState(null);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [officeToDelete, setOfficeToDelete] = useState(null);

  // Categories configuration
  const categories = [
  { id: 'board', label: 'مجلس الإدارة' },
  { id: 'supervisory', label: 'الهيئة الإشرافية' },
  { id: 'electoral', label: 'اللجنة الانتخابية' },
  { id: 'general_assembly', label: 'الهيئة العمومية' },
  { id: 'offices', label: 'المكاتب' }];


  // Fetch offices from database
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        const data = await officesService.getAll();
        
        // Map database response to component expected format
        const mappedOffices = data?.map((office) => ({
          id: office?.id,
          name: office?.headNameAr || office?.headNameEn || office?.headNameTr || office?.nameAr || office?.nameEn || 'Unknown',
          title: office?.positionAr || office?.positionEn || office?.positionTr || '',
          department: office?.nameAr || office?.nameEn || office?.nameTr || '',
          category: office?.category || 'offices',
          email: office?.email || '',
          phone: office?.phone || '',
          profileImage: office?.imageUrl || '',
          profileImageAlt: `${office?.headNameEn || office?.headNameAr || 'Office'} profile image`,
          status: "active",
          description: "",
          responsibilities: [],
          appointmentDate: "",
          order: office?.orderIndex || 0,
          createdAt: office?.createdAt || new Date().toISOString(),
          updatedAt: office?.updatedAt || new Date().toISOString()
        }));

        setOffices(mappedOffices);
      } catch (err) {
        console.error('Error fetching offices:', err);
        error('فشل في تحميل البيانات');
        setOffices([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [error]);

  // Filter offices based on category and search
  useEffect(() => {
    let filtered = offices?.filter((office) => office?.category === activeCategory);

    if (searchQuery?.trim()) {
      filtered = filtered?.filter((office) =>
      office?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      office?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      office?.department?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      office?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase())
      );
    }

    // Sort by order
    filtered?.sort((a, b) => a?.order - b?.order);

    setFilteredOffices(filtered);
  }, [offices, activeCategory, searchQuery]);

  // Get office counts by category
  const getOfficeCounts = () => {
    const counts = {};
    categories?.forEach((category) => {
      counts[category.id] = offices?.filter((office) => office?.category === category?.id)?.length;
    });
    return counts;
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e?.ctrlKey || e?.metaKey) && e?.key === 'k') {
        e?.preventDefault();
        setIsSearchOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Event handlers
  const handleOfficeSelect = (officeId) => {
    setSelectedOffices((prev) =>
    prev?.includes(officeId) ?
    prev?.filter((id) => id !== officeId) :
    [...prev, officeId]
    );
  };

  const handleSelectAll = () => {
    if (selectedOffices?.length === filteredOffices?.length) {
      setSelectedOffices([]);
    } else {
      setSelectedOffices(filteredOffices?.map((office) => office?.id));
    }
  };

  const handleClearSelection = () => {
    setSelectedOffices([]);
  };

  const handleOfficeReorder = async (reorderedOffices) => {
    try {
      // Fetch full office data from database to get all fields
      const allOfficesData = await officesService.getAll();
      
      // Update order_index for each office in the database
      await Promise.all(
        reorderedOffices.map(async (office, index) => {
          // Find the full office data from database
          const dbOffice = allOfficesData.find(o => o.id === office.id);
          if (!dbOffice) return Promise.resolve();

          // Update only the order_index, keeping all other fields
          const updatePayload = {
            nameAr: dbOffice.nameAr,
            nameEn: dbOffice.nameEn,
            nameTr: dbOffice.nameTr,
            headNameAr: dbOffice.headNameAr,
            headNameEn: dbOffice.headNameEn,
            headNameTr: dbOffice.headNameTr,
            positionAr: dbOffice.positionAr,
            positionEn: dbOffice.positionEn,
            positionTr: dbOffice.positionTr,
            email: dbOffice.email,
            phone: dbOffice.phone,
            imageUrl: dbOffice.imageUrl,
            category: dbOffice.category,
            orderIndex: index
          };

          return officesService.update(office.id, updatePayload);
        })
      );

      // Refresh data from database
      const data = await officesService.getAll();
      const mappedOffices = data?.map((office) => ({
        id: office?.id,
        name: office?.headNameAr || office?.headNameEn || office?.headNameTr || office?.nameAr || office?.nameEn || 'Unknown',
        title: office?.positionAr || office?.positionEn || office?.positionTr || '',
        department: office?.nameAr || office?.nameEn || office?.nameTr || '',
        category: office?.category || 'offices',
        email: office?.email || '',
        phone: office?.phone || '',
        profileImage: office?.imageUrl || '',
        profileImageAlt: `${office?.headNameEn || office?.headNameAr || 'Office'} profile image`,
        status: "active",
        description: "",
        responsibilities: [],
        appointmentDate: "",
        order: office?.orderIndex || 0,
        createdAt: office?.createdAt || new Date().toISOString(),
        updatedAt: office?.updatedAt || new Date().toISOString()
      }));

      setOffices(mappedOffices);
      success('تم إعادة ترتيب المناصب بنجاح');
    } catch (err) {
      console.error('Error reordering offices:', err);
      error('فشل في إعادة ترتيب المناصب');
    }
  };

  const handleOfficeEdit = (office) => {
    window.location.href = `/office-form?id=${office?.id}`;
  };

  const handleOfficeView = (office) => {
    setSelectedOffice(office);
    setIsDetailsModalOpen(true);
  };

  const handleOfficeDelete = (office) => {
    setOfficeToDelete(office);
    setIsDeleteModalOpen(true);
  };

  const handleBulkDelete = () => {
    if (selectedOffices?.length === 0) return;
    setOfficeToDelete(null);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    try {
      if (officeToDelete) {
        // Single delete
        await officesService.delete(officeToDelete.id);
        setOffices((prev) => prev?.filter((office) => office?.id !== officeToDelete?.id));
        success(`تم حذف منصب ${officeToDelete?.name} بنجاح`);
      } else {
        // Bulk delete
        await Promise.all(selectedOffices.map(id => officesService.delete(id)));
        const count = selectedOffices.length;
        setOffices((prev) => prev?.filter((office) => !selectedOffices?.includes(office?.id)));
        success(`تم حذف ${count} منصب بنجاح`);
        setSelectedOffices([]);
      }
      setIsDeleteModalOpen(false);
      setOfficeToDelete(null);
    } catch (err) {
      console.error('Error deleting office:', err);
      error('فشل في حذف المنصب');
    }
  };

  const handleBulkExport = () => {
    const selectedData = offices?.filter((office) => selectedOffices?.includes(office?.id));
    const csvContent = "data:text/csv;charset=utf-8," + "الاسم,المنصب,القسم,البريد الإلكتروني,الهاتف,الحالة\n" +
    selectedData?.map((office) =>
    `"${office?.name}","${office?.title}","${office?.department}","${office?.email}","${office?.phone}","${office?.status}"`
    )?.join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link?.setAttribute("href", encodedUri);
    link?.setAttribute("download", `offices_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);

    success(`تم تصدير ${selectedOffices?.length} منصب بنجاح`);
  };

  const handleBulkCategoryChange = async (newCategory) => {
    try {
      await Promise.all(
        selectedOffices.map(id => {
          const office = offices.find(o => o.id === id);
          return office ? officesService.update(id, { ...office, category: newCategory }) : Promise.resolve();
        })
      );
      
      // Refresh data
      const data = await officesService.getAll();
      const mappedOffices = data?.map((office) => ({
        id: office?.id,
        name: office?.headNameAr || office?.headNameEn || office?.headNameTr || office?.nameAr || office?.nameEn || 'Unknown',
        title: office?.positionAr || office?.positionEn || office?.positionTr || '',
        department: office?.nameAr || office?.nameEn || office?.nameTr || '',
        category: office?.category || 'offices',
        email: office?.email || '',
        phone: office?.phone || '',
        profileImage: office?.imageUrl || '',
        profileImageAlt: `${office?.headNameEn || office?.headNameAr || 'Office'} profile image`,
        status: "active",
        description: "",
        responsibilities: [],
        appointmentDate: "",
        order: office?.orderIndex || 0,
        createdAt: office?.createdAt || new Date().toISOString(),
        updatedAt: office?.updatedAt || new Date().toISOString()
      }));
      
      setOffices(mappedOffices);
      success(`تم نقل ${selectedOffices?.length} منصب إلى فئة جديدة`);
      setSelectedOffices([]);
    } catch (err) {
      console.error('Error changing category:', err);
      error('فشل في نقل المناصب');
    }
  };

  const handleAddOffice = () => {
    window.location.href = `/office-form?category=${activeCategory}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)} />
        <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
        <main className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pt-16`}>
          <div className="p-6">
            <div className="animate-pulse space-y-6">
              <div className="h-8 bg-muted rounded w-1/3"></div>
              <div className="h-32 bg-muted rounded"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)]?.map((_, i) =>
                <div key={i} className="h-48 bg-muted rounded"></div>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>);

  }

  return (
    <div className="min-h-screen bg-background" dir="rtl">
      <Header
        onMenuToggle={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        isMenuOpen={isMobileMenuOpen} />

      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <main className={`transition-all duration-300 ${isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-60'} pt-16`}>
        <div className="p-6 space-y-6">
          {/* Page Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <Breadcrumbs />
              <h1 className="text-2xl font-bold text-foreground mt-2 font-arabic">
                إدارة المناصب
              </h1>
              <p className="text-muted-foreground mt-1">
                إدارة المناصب الإدارية والتنظيمية للاتحاد
              </p>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                onClick={() => setIsSearchOpen(true)}
                iconName="Search"
                iconPosition="left"
                iconSize={16}>

                بحث (Ctrl+K)
              </Button>

              <Button
                variant="default"
                onClick={handleAddOffice}
                iconName="Plus"
                iconPosition="left"
                iconSize={16}>

                إضافة منصب
              </Button>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          <BulkActionsBar
            selectedCount={selectedOffices?.length}
            onClearSelection={handleClearSelection}
            onBulkDelete={handleBulkDelete}
            onBulkExport={handleBulkExport}
            onBulkCategoryChange={handleBulkCategoryChange}
            categories={categories} />


          {/* Category Tabs */}
          <CategoryTabs
            categories={categories}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            officeCounts={getOfficeCounts()}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery} />


          {/* Office List */}
          <div className="bg-card border border-border rounded-lg">
            {/* List Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={selectedOffices?.length === filteredOffices?.length && filteredOffices?.length > 0}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2" />

                  <span className="text-sm text-muted-foreground">
                    تحديد الكل ({filteredOffices?.length})
                  </span>
                </div>
                
                {selectedOffices?.length > 0 &&
                <span className="text-sm text-primary font-medium">
                    تم تحديد {selectedOffices?.length} منصب
                  </span>
                }
              </div>

              <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                <Icon name="GripVertical" size={16} />
                <span>اسحب لإعادة الترتيب</span>
              </div>
            </div>

            {/* Office Cards */}
            <div className="p-4">
              <DragDropOfficeList
                offices={filteredOffices}
                onReorder={handleOfficeReorder}
                selectedOffices={selectedOffices}
                onOfficeSelect={handleOfficeSelect}
                onOfficeEdit={handleOfficeEdit}
                onOfficeDelete={handleOfficeDelete}
                onOfficeView={handleOfficeView} />

            </div>
          </div>

          {/* Empty State */}
          {filteredOffices?.length === 0 && !isLoading &&
          <div className="text-center py-12">
              <div className="w-16 h-16 mx-auto mb-4 bg-muted rounded-full flex items-center justify-center">
                <Icon name="Briefcase" size={24} className="text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2 font-arabic">
                {searchQuery ? 'لا توجد نتائج' : 'لا توجد مناصب'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ?
              `لم يتم العثور على مناصب تطابق "${searchQuery}"` :
              'ابدأ بإضافة منصب جديد لهذه الفئة'
              }
              </p>
              {!searchQuery &&
            <Button
              variant="default"
              onClick={handleAddOffice}
              iconName="Plus"
              iconPosition="left"
              iconSize={16}>

                  إضافة منصب جديد
                </Button>
            }
            </div>
          }
        </div>
      </main>
      {/* Modals */}
      <OfficeDetailsModal
        office={selectedOffice}
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        onEdit={handleOfficeEdit} />

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        office={officeToDelete}
        isMultiple={!officeToDelete}
        selectedCount={selectedOffices?.length} />

      <SearchCommandPalette
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)} />

      <QuickActionButton />
    </div>);

};

const OfficesManagement = () => {
  return (
    <NotificationProvider>
      <OfficesManagementContent />
    </NotificationProvider>);

};

export default OfficesManagement;